import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { NotificationProvider } from './context/NotificationContext';
import { Toaster } from './components/ui/sonner';
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import ActivateSubAccount from './pages/ActivateSubAccount';
import Dashboard from './pages/Dashboard';
import Messagerie from './pages/Messagerie';
// import Incidents from './pages/Incidents';  // DEPRECATED: Replaced by IncidentsSinistres
import Assemblees from './pages/Assemblees';
import MonCompte from './pages/MonCompte';
import MonCabinet from './pages/MonCabinet';
import Immeubles from './pages/Immeubles';
import Coproprietaires from './pages/Coproprietaires';
import Equipe from './pages/Equipe';
import Prestataires from './pages/Prestataires';
import Evenements from './pages/Evenements';
import Annonces from './pages/Annonces';
import Modules from './pages/Modules';
import Factures from './pages/Factures';
import SousComptes from './pages/SousComptes';
import AdminDashboard from './pages/AdminDashboard';
import AdminCabinets from './pages/AdminCabinets';
import AdminUtilisateurs from './pages/AdminUtilisateurs';
import AdminProduits from './pages/AdminProduits';
import ActivityTracker from './pages/ActivityTracker';
import Sondages from './pages/Sondages';
import Newsletters from './pages/Newsletters';
import OCRFactures from './pages/OCRFactures';
import PredictiveDashboard from './pages/PredictiveDashboard';
import Aide from './pages/Aide';
import Glossaire from './pages/Glossaire';
import Visites from './pages/Visites';
import ComptabiliteDashboard from './pages/Comptabilite/ComptabiliteDashboard';
import CarnetEntretien from './pages/CarnetEntretien';
import NotificationAnalytics from './pages/NotificationAnalytics';
// import Sinistres from './pages/Sinistres';  // DEPRECATED: Replaced by IncidentsSinistres
import Contrats from './pages/Contrats';
import MesCharges from './pages/MesCharges';
import ParametresNotifications from './pages/ParametresNotifications';
import NotificationsPage from './pages/NotificationsPage';
import NavigationStats from './pages/NavigationStats';
import ExpenseApproval from './pages/ExpenseApproval';
import RGPDArchivage from './pages/RGPDArchivage';
import IncidentsSinistres from './pages/IncidentsSinistres';
import DashboardComptable from './pages/DashboardComptable';
import DocumentsJuridiques from './pages/DocumentsJuridiques';
import RolesManagement from './pages/RolesManagement';
import Layout from './components/Layout';
import { 
  WebsiteHome, 
  WebsiteApplication, 
  WebsiteRenovation, 
  WebsiteEquipe, 
  WebsiteContact, 
  WebsiteAdmin 
} from './website';
import './App.css';

const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#00BCD4]"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" />;
  }
  
  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to="/" />;
  }
  
  return <Layout>{children}</Layout>;
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <NotificationProvider>
          <Toaster />
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/activate-subaccount" element={<ActivateSubAccount />} />
          
          <Route path="/" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
          <Route path="/messagerie" element={<ProtectedRoute><Messagerie /></ProtectedRoute>} />
          {/* /incidents route redirects to unified module */}
          <Route path="/incidents" element={<Navigate to="/incidents-sinistres" replace />} />
          <Route path="/assemblees" element={<ProtectedRoute><Assemblees /></ProtectedRoute>} />
          <Route path="/mon-compte" element={<ProtectedRoute><MonCompte /></ProtectedRoute>} />
          
          {/* Phase 5: Événements & Annonces */}
          <Route path="/notifications" element={<ProtectedRoute><NotificationsPage /></ProtectedRoute>} />
          <Route path="/evenements" element={<ProtectedRoute><Evenements /></ProtectedRoute>} />
          <Route path="/annonces" element={<ProtectedRoute><Annonces /></ProtectedRoute>} />
          <Route path="/sondages" element={<ProtectedRoute><Sondages /></ProtectedRoute>} />
          <Route path="/newsletters" element={<ProtectedRoute><Newsletters /></ProtectedRoute>} />
          <Route path="/ocr-factures" element={<ProtectedRoute allowedRoles={['gestionnaire', 'admin', 'superadmin', 'directeur_cabinet', 'comptable', 'assistant']}><OCRFactures /></ProtectedRoute>} />
          <Route path="/dashboard-predictif" element={<ProtectedRoute allowedRoles={['gestionnaire', 'admin', 'superadmin', 'directeur_cabinet', 'comptable', 'assistant']}><PredictiveDashboard /></ProtectedRoute>} />
          
          {/* Phase 4: Modules & Factures */}
          <Route path="/modules" element={<ProtectedRoute><Modules /></ProtectedRoute>} />
          <Route path="/mes-factures" element={<ProtectedRoute><Factures /></ProtectedRoute>} />
          
          {/* Phase 6: Gestion Immobilière (Gestionnaire) */}
          <Route path="/immeubles" element={<ProtectedRoute allowedRoles={['gestionnaire', 'admin', 'superadmin', 'directeur_cabinet', 'comptable']}><Immeubles /></ProtectedRoute>} />
          <Route path="/coproprietaires" element={<ProtectedRoute allowedRoles={['gestionnaire', 'admin', 'superadmin', 'directeur_cabinet', 'comptable']}><Coproprietaires /></ProtectedRoute>} />
          <Route path="/equipe" element={<ProtectedRoute allowedRoles={['gestionnaire', 'admin', 'superadmin', 'directeur_cabinet']}><Equipe /></ProtectedRoute>} />
          <Route path="/prestataires" element={<ProtectedRoute allowedRoles={['gestionnaire', 'admin', 'superadmin', 'directeur_cabinet']}><Prestataires /></ProtectedRoute>} />
          <Route path="/visites" element={<ProtectedRoute allowedRoles={['gestionnaire', 'directeur_cabinet', 'prestataire', 'admin', 'superadmin']}><Visites /></ProtectedRoute>} />
          <Route path="/comptabilite" element={<ProtectedRoute allowedRoles={['gestionnaire', 'directeur_cabinet', 'comptable', 'admin', 'superadmin']}><ComptabiliteDashboard /></ProtectedRoute>} />
          <Route path="/grand-livre" element={<Navigate to="/comptabilite?tab=grand-livre" replace />} />
          <Route path="/carnet-entretien" element={<ProtectedRoute allowedRoles={['gestionnaire', 'directeur_cabinet', 'admin', 'superadmin']}><CarnetEntretien /></ProtectedRoute>} />
          {/* /sinistres route redirects to unified module */}
          <Route path="/sinistres" element={<Navigate to="/incidents-sinistres" replace />} />
          <Route path="/contrats" element={<ProtectedRoute allowedRoles={['gestionnaire', 'directeur_cabinet', 'comptable', 'assistant', 'admin', 'superadmin']}><Contrats /></ProtectedRoute>} />
          <Route path="/approbation-depenses" element={<ProtectedRoute allowedRoles={['gestionnaire', 'directeur_cabinet', 'comptable', 'assistant', 'admin', 'superadmin']}><ExpenseApproval /></ProtectedRoute>} />
          <Route path="/rgpd-archivage" element={<ProtectedRoute allowedRoles={['gestionnaire', 'directeur_cabinet', 'admin', 'superadmin']}><RGPDArchivage /></ProtectedRoute>} />
          <Route path="/incidents-sinistres" element={<ProtectedRoute allowedRoles={['gestionnaire', 'directeur_cabinet', 'comptable', 'assistant', 'admin', 'superadmin', 'coproprietaire', 'prestataire']}><IncidentsSinistres /></ProtectedRoute>} />
          <Route path="/documents-juridiques" element={<ProtectedRoute allowedRoles={['gestionnaire', 'directeur_cabinet', 'directeur', 'comptable', 'assistant', 'admin', 'superadmin']}><DocumentsJuridiques /></ProtectedRoute>} />
          <Route path="/mes-charges" element={<ProtectedRoute allowedRoles={['coproprietaire', 'admin', 'superadmin']}><MesCharges /></ProtectedRoute>} />
          
          {/* Phase 7: Admin */}
          <Route path="/admin" element={<ProtectedRoute allowedRoles={['admin', 'superadmin']}><AdminDashboard /></ProtectedRoute>} />
          <Route path="/admin/cabinets" element={<ProtectedRoute allowedRoles={['admin', 'superadmin']}><AdminCabinets /></ProtectedRoute>} />
          <Route path="/admin/produits" element={<ProtectedRoute allowedRoles={['admin', 'superadmin']}><AdminProduits /></ProtectedRoute>} />
          <Route path="/admin/commandes" element={<ProtectedRoute allowedRoles={['admin', 'superadmin']}><Factures /></ProtectedRoute>} />
          <Route path="/admin/utilisateurs" element={<ProtectedRoute allowedRoles={['admin', 'superadmin', 'directeur_cabinet']}><AdminUtilisateurs /></ProtectedRoute>} />
          <Route path="/admin/activite" element={<ProtectedRoute allowedRoles={['admin', 'superadmin']}><ActivityTracker /></ProtectedRoute>} />
          <Route path="/admin/roles" element={<ProtectedRoute allowedRoles={['admin', 'superadmin']}><RolesManagement /></ProtectedRoute>} />
          
          {/* Placeholder routes */}
          <Route path="/mon-cabinet" element={<ProtectedRoute allowedRoles={['gestionnaire', 'directeur_cabinet']}><MonCabinet /></ProtectedRoute>} />
          <Route path="/sous-comptes" element={<ProtectedRoute allowedRoles={['coproprietaire']}><SousComptes /></ProtectedRoute>} />
          
          {/* Help / Aide page */}
          <Route path="/aide" element={<ProtectedRoute allowedRoles={['gestionnaire', 'coproprietaire', 'prestataire', 'sous-compte', 'admin', 'superadmin', 'directeur_cabinet']}><Aide /></ProtectedRoute>} />
          <Route path="/glossaire" element={<ProtectedRoute allowedRoles={['gestionnaire', 'coproprietaire', 'prestataire', 'sous-compte', 'admin', 'superadmin', 'directeur_cabinet']}><Glossaire /></ProtectedRoute>} />
          
          {/* Settings */}
          <Route path="/parametres" element={<ProtectedRoute allowedRoles={['gestionnaire', 'coproprietaire', 'prestataire', 'sous-compte', 'admin', 'superadmin', 'directeur_cabinet', 'comptable', 'assistant']}><ParametresNotifications /></ProtectedRoute>} />
          
          {/* Analytics */}
          <Route path="/analytics/notifications" element={<ProtectedRoute allowedRoles={['gestionnaire', 'admin', 'superadmin', 'directeur_cabinet']}><NotificationAnalytics /></ProtectedRoute>} />
          <Route path="/mes-statistiques" element={<ProtectedRoute allowedRoles={['gestionnaire', 'coproprietaire', 'prestataire', 'sous-compte', 'admin', 'superadmin', 'directeur_cabinet', 'comptable', 'assistant']}><NavigationStats /></ProtectedRoute>} />
          
          {/* Website Pages (Public) */}
          <Route path="/site" element={<WebsiteHome />} />
          <Route path="/site/application" element={<WebsiteApplication />} />
          <Route path="/site/renovation" element={<WebsiteRenovation />} />
          <Route path="/site/equipe" element={<WebsiteEquipe />} />
          <Route path="/site/contact" element={<WebsiteContact />} />
          <Route path="/site/admin" element={<WebsiteAdmin />} />
          
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
        </NotificationProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
